/**
 * @license
 * Copyright 2026 okayest! studios LLC. All Rights Reserved.
 */

/**
 * @fileoverview Row counter data model and count math, mirroring the
 * ProjectCounters model in the Yarnly app.
 */

export interface RowCounter {
  id: string;
  label: string;
  /** Absolute row count, not accounting for repeats. */
  rows: number;
  parentId: string | null;
  rowsInRepeat: number | null;
  startAtOne: boolean;
  /** Epoch milliseconds of the last change. */
  updated: number;
}

export interface RowCounterState {
  counters: RowCounter[];
  lastUsedCounterId: string | null;
}

export const TOTAL_ROWS_LABEL = 'Total Rows';

export function startCount(counter: RowCounter): number {
  return counter.startAtOne ? 1 : 0;
}

/** Displayable row count, accounting for resets due to repeats. */
export function calculateCount(rows: number, start: number, rowsInRepeat: number | null): number {
  if (rowsInRepeat !== null) {
    return mod(rows - start, rowsInRepeat) + start;
  }
  return rows;
}

/** Number of repeats (resets) that have occurred. */
export function calculateRepeats(rows: number, start: number, rowsInRepeat: number | null): number {
  if (rowsInRepeat === null) {
    return 0;
  }
  return Math.floor((rows - start) / rowsInRepeat);
}

export function getCounter(state: RowCounterState, id: string): RowCounter | null {
  return state.counters.find((c) => c.id === id) ?? null;
}

export function getSubcounters(state: RowCounterState, parentId: string): RowCounter[] {
  return state.counters.filter((c) => c.parentId === parentId);
}

/** Total rows from linked subcounters, excluding the parent's own rows. */
export function getSubcounterCount(state: RowCounterState, parentId: string): number {
  return getSubcounters(state, parentId).reduce((sum, c) => sum + c.rows, 0);
}

/** Total rows including linked subcounters, without accounting for repeats. */
export function getTotalCount(state: RowCounterState, id: string): number {
  const counter = getCounter(state, id);
  if (counter === null) {
    return 0;
  }
  return counter.rows + getSubcounterCount(state, id);
}

/** Displayed row count, including linked subcounters and repeat resets. */
export function getCount(state: RowCounterState, id: string): number {
  const counter = getCounter(state, id);
  if (counter === null) {
    return 0;
  }
  return calculateCount(getTotalCount(state, id), startCount(counter), counter.rowsInRepeat);
}

export function getRepeats(state: RowCounterState, id: string): number {
  const counter = getCounter(state, id);
  if (counter === null) {
    return 0;
  }
  return calculateRepeats(counter.rows, startCount(counter), counter.rowsInRepeat);
}

/** Dart-style modulo, which is always non-negative for a positive divisor. */
function mod(a: number, b: number): number {
  return ((a % b) + b) % b;
}
